/* ==========================================================================
   REAL STREET COMEDY — theme.js
   Vanilla ES5+/ES2017. No frameworks, no CDNs.
   Every module queries its own elements first and returns early if absent,
   so a missing section can never break the rest of the page.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;

  var Theme = (window.RSC = window.RSC || {});
  Theme.routes = Theme.routes || {};
  Theme.strings = Theme.strings || {};

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function on(el, evt, fn, opts) { if (el && el.addEventListener) el.addEventListener(evt, fn, opts); }

  function money(cents) {
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: Theme.currency || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }
  Theme.money = money;

  /* ------------------------------------------------------------------------
     Scroll lock — reference-counted so nested overlays cannot unlock early
     ------------------------------------------------------------------------ */
  var lockCount = 0;
  function lockScroll() {
    lockCount += 1;
    root.classList.add('no-scroll');
  }
  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) root.classList.remove('no-scroll');
  }

  /* ------------------------------------------------------------------------
     Overlay controller — powers mobile nav, cart drawer and search
     ------------------------------------------------------------------------ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var openOverlays = [];

  function trapFocus(e, panel) {
    if (e.key !== 'Tab') return;
    var items = $$(FOCUSABLE, panel).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openOverlay(el, opener) {
    if (!el || el.classList.contains('is-open')) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    el.__opener = opener || document.activeElement;
    lockScroll();
    openOverlays.push(el);

    var panel = el.querySelector('[data-overlay-panel]') || el;
    el.__keydown = function (e) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeOverlay(el);
      } else {
        trapFocus(e, panel);
      }
    };
    on(el, 'keydown', el.__keydown);

    window.setTimeout(function () {
      var target = el.querySelector('[data-overlay-focus]') || $$(FOCUSABLE, panel)[0];
      if (target) target.focus();
    }, 60);

    document.dispatchEvent(new CustomEvent('rsc:overlay:open', { detail: { overlay: el } }));
  }

  function closeOverlay(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    if (el.__keydown) el.removeEventListener('keydown', el.__keydown);
    unlockScroll();
    openOverlays = openOverlays.filter(function (o) { return o !== el; });
    if (el.__opener && typeof el.__opener.focus === 'function') el.__opener.focus();
    el.__opener = null;
  }

  Theme.openOverlay = openOverlay;
  Theme.closeOverlay = closeOverlay;

  function initOverlays() {
    // Any element with data-overlay-open="<id>" toggles that overlay open
    on(document, 'click', function (e) {
      var opener = e.target.closest ? e.target.closest('[data-overlay-open]') : null;
      if (opener) {
        var id = opener.getAttribute('data-overlay-open');
        var overlay = document.getElementById(id);
        if (overlay) {
          e.preventDefault();
          openOverlay(overlay, opener);
        }
        return;
      }
      var closer = e.target.closest ? e.target.closest('[data-overlay-close]') : null;
      if (closer) {
        var host = closer.closest('[data-overlay]');
        if (host) {
          e.preventDefault();
          closeOverlay(host);
        }
      }
    });
  }

  /* ------------------------------------------------------------------------
     Disclosures — mobile nav, product accordions, the details panel.

     Panels are server-rendered OPEN so their content is readable with JS
     disabled. Anything marked [data-collapse-on-load] is closed here, at
     runtime, only once we know JS can reopen it.
     ------------------------------------------------------------------------ */
  function setExpanded(trigger, expanded) {
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var id = trigger.getAttribute('aria-controls');
    if (!id) return; // adjacent-sibling panels are handled by CSS
    var panel = document.getElementById(id);
    if (panel) panel.hidden = !expanded;
  }

  function initDisclosures(scope) {
    $$('[data-collapse-on-load]', scope || document).forEach(function (trigger) {
      setExpanded(trigger, false);
    });
  }

  function bindDisclosures() {
    on(document, 'click', function (e) {
      var trigger = e.target.closest ? e.target.closest('[data-toggle-expand]') : null;
      if (!trigger) return;
      e.preventDefault();
      setExpanded(trigger, trigger.getAttribute('aria-expanded') !== 'true');
    });
  }

  /* ------------------------------------------------------------------------
     Scroll reveal — never hides anything that is already in view, and does
     nothing at all when IntersectionObserver is unavailable.
     ------------------------------------------------------------------------ */
  function initReveal() {
    var targets = $$('[data-reveal]');
    if (!targets.length) return;
    if (root.classList.contains('no-anim')) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    var fold = window.innerHeight * 0.92;
    var below = targets.filter(function (el) {
      return el.getBoundingClientRect().top > fold;
    });
    if (!below.length) return;

    // Only now — after we know they are off-screen — do we hide them.
    below.forEach(function (el) { el.classList.add('reveal'); });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    below.forEach(function (el) { io.observe(el); });
  }

  /* ------------------------------------------------------------------------
     Sticky mobile buy bar — appears once the buy block scrolls away
     ------------------------------------------------------------------------ */
  function initStickyBuy() {
    var bar = $('[data-sticky-buy]');
    if (!bar) return;
    var anchorId = bar.getAttribute('data-anchor');
    var anchor = anchorId ? document.getElementById(anchorId) : null;

    function update() {
      var narrow = window.innerWidth < 860;
      var past = anchor ? window.scrollY > anchor.offsetTop + 200 : window.scrollY > 600;
      var overlayOpen = openOverlays.length > 0;
      bar.classList.toggle('is-visible', narrow && past && !overlayOpen);
    }

    on(window, 'scroll', update, { passive: true });
    on(window, 'resize', update);
    on(document, 'rsc:overlay:open', update);
    update();
  }

  /* ------------------------------------------------------------------------
     Announcement marquee — duplicates the track so the loop is seamless
     ------------------------------------------------------------------------ */
  function initMarquee() {
    $$('[data-marquee]').forEach(function (track) {
      if (track.getAttribute('data-marquee-ready') === 'true') return;
      track.innerHTML = track.innerHTML + track.innerHTML;
      track.setAttribute('data-marquee-ready', 'true');
    });
  }

  /* ------------------------------------------------------------------------
     Episodes — the player is a thumbnail button until clicked, then swaps in a
     youtube-nocookie iframe. List cards load into the main player instead of
     leaving the site; without JS they are plain links to YouTube.
     ------------------------------------------------------------------------ */
  function playEpisode(button, autoplay) {
    var id = button.getAttribute('data-yt-lite');
    if (!id) return;
    var frame = document.createElement('iframe');
    frame.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) +
      '?rel=0&playsinline=1' + (autoplay ? '&autoplay=1' : '');
    frame.title = button.getAttribute('data-yt-title') || 'YouTube video';
    frame.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.allowFullscreen = true;
    frame.className = 'yt-lite__frame';
    var holder = document.createElement('div');
    holder.className = 'yt-lite yt-lite--playing';
    holder.setAttribute('data-yt-holder', id);
    holder.appendChild(frame);
    button.parentNode.replaceChild(holder, button);
  }

  function initEpisodes() {
    $$('[data-episodes]').forEach(function (wrap) {
      if (wrap.getAttribute('data-episodes-ready') === 'true') return;
      wrap.setAttribute('data-episodes-ready', 'true');

      var player = $('.episodes__player', wrap);

      on(wrap, 'click', function (e) {
        var lite = e.target.closest('[data-yt-lite]');
        if (lite) {
          playEpisode(lite, true);
          return;
        }

        var card = e.target.closest('[data-episode-swap]');
        if (!card || !player) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey) return;
        e.preventDefault();

        var id = card.getAttribute('data-episode-swap');
        var title = card.getAttribute('data-episode-title') || '';
        var meta = card.getAttribute('data-episode-meta') || '';

        var current = $('[data-yt-lite], [data-yt-holder]', player);
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'yt-lite';
        button.setAttribute('data-yt-lite', id);
        button.setAttribute('data-yt-title', title);
        if (current) current.parentNode.replaceChild(button, current);

        var titleEl = $('[data-episode-title]', player);
        var metaEl = $('[data-episode-meta]', player);
        if (titleEl) titleEl.textContent = title;
        if (metaEl) metaEl.textContent = meta;

        $$('.episode-card.is-active', wrap).forEach(function (el) { el.classList.remove('is-active'); });
        card.classList.add('is-active');

        playEpisode(button, true);

        var top = player.getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.5) {
          player.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    });
  }

  /* ------------------------------------------------------------------------
     Cart — AJAX add / change, drawer re-rendered via the Section Rendering API
     ------------------------------------------------------------------------ */
  var Cart = (Theme.cart = {});

  function sectionUrl(section) {
    var base = Theme.routes.root || '/';
    if (base.charAt(base.length - 1) !== '/') base += '/';
    return base + '?sections=' + encodeURIComponent(section);
  }

  function renderDrawer() {
    var host = $('[data-cart-drawer-inner]');
    if (!host) return Promise.resolve();
    return fetch(sectionUrl('cart-drawer'), { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data['cart-drawer'];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-cart-drawer-inner]');
        if (fresh) host.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* leave the existing markup in place */ });
  }

  function updateCount(count) {
    $$('[data-cart-count]').forEach(function (el) { el.textContent = count; });
    $$('[data-cart-count-wrap]').forEach(function (el) {
      el.classList.toggle('is-empty', Number(count) === 0);
    });
  }

  function refreshCount() {
    return fetch((Theme.routes.cart || '/cart') + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCount(cart.item_count); return cart; })
      .catch(function () { return null; });
  }

  Cart.add = function (formDataOrBody, opts) {
    opts = opts || {};
    var body;
    var headers = { 'Accept': 'application/json' };
    if (formDataOrBody instanceof FormData) {
      formDataOrBody.delete('sections');
      body = formDataOrBody;
    } else {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(formDataOrBody);
    }
    return fetch((Theme.routes.cartAdd || '/cart/add') + '.js', {
      method: 'POST',
      headers: headers,
      body: body
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) throw res.data;
        return refreshCount().then(renderDrawer).then(function () {
          if (opts.openDrawer !== false) {
            var drawer = document.getElementById('cart-drawer-overlay');
            if (drawer) openOverlay(drawer, opts.opener);
          }
          document.dispatchEvent(new CustomEvent('rsc:cart:added', { detail: res.data }));
          return res.data;
        });
      });
  };

  Cart.change = function (payload) {
    return fetch((Theme.routes.cartChange || '/cart/change') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        updateCount(cart.item_count);
        return renderDrawer().then(function () { return cart; });
      });
  };

  function initCartDrawer() {
    // Quantity steppers and remove links inside the drawer (delegated, so the
    // handlers survive every re-render).
    on(document, 'click', function (e) {
      if (!e.target.closest) return;

      var step = e.target.closest('[data-qty-step]');
      if (step) {
        var wrap = step.closest('.qty');
        var input = wrap ? wrap.querySelector('input') : null;
        if (input) {
          var delta = step.getAttribute('data-qty-step') === 'up' ? 1 : -1;
          var next = Math.max(Number(input.min || 0), (parseInt(input.value, 10) || 0) + delta);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        e.preventDefault();
        return;
      }

      var remove = e.target.closest('[data-cart-remove]');
      if (remove && remove.closest('[data-cart-drawer-inner]')) {
        e.preventDefault();
        remove.setAttribute('aria-busy', 'true');
        Cart.change({ line: Number(remove.getAttribute('data-line')), quantity: 0 }).catch(function () {
          window.location.href = remove.getAttribute('href') || (Theme.routes.cart || '/cart');
        });
      }
    });

    on(document, 'change', function (e) {
      var input = e.target.closest ? e.target.closest('[data-cart-qty]') : null;
      if (!input) return;
      var line = Number(input.getAttribute('data-line'));
      var qty = Math.max(0, parseInt(input.value, 10) || 0);
      if (!line) return;
      // Only hijack the drawer; the cart page posts natively so it works JS-free.
      if (!input.closest('[data-cart-drawer-inner]')) return;
      Cart.change({ line: line, quantity: qty }).catch(function () { });
    });
  }

  /* ------------------------------------------------------------------------
     Product forms — progressive enhancement over the native <form>
     ------------------------------------------------------------------------ */
  function initProductForms() {
    on(document, 'submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('[data-product-form]')) return;
      if (Theme.cartType !== 'drawer') return; // let the browser POST to /cart/add

      e.preventDefault();
      var btn = form.querySelector('[data-add-button]');
      var label = btn ? btn.querySelector('[data-add-label]') : null;
      var original = label ? label.textContent : '';
      var errorBox = form.querySelector('[data-form-error]');

      if (btn) { btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
      if (label) label.textContent = Theme.strings.adding || 'Adding…';
      if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }

      Cart.add(new FormData(form), { opener: btn })
        .catch(function (err) {
          var msg = (err && (err.description || err.message)) || Theme.strings.cartError || 'Could not add to cart.';
          if (errorBox) { errorBox.textContent = msg; errorBox.hidden = false; }
        })
        .then(function () {
          if (btn) { btn.disabled = false; btn.removeAttribute('aria-busy'); }
          if (label) label.textContent = original;
        });
    });

    // Quick-add buttons on product cards (single-variant products)
    on(document, 'click', function (e) {
      var btn = e.target.closest ? e.target.closest('[data-quick-add]') : null;
      if (!btn || btn.disabled) return;
      var id = btn.getAttribute('data-variant-id');
      if (!id) return;
      e.preventDefault();
      var original = btn.textContent;
      btn.disabled = true;
      btn.textContent = Theme.strings.adding || 'Adding…';
      Cart.add({ items: [{ id: Number(id), quantity: 1 }] }, { opener: btn })
        .catch(function () { window.location.href = Theme.routes.cart || '/cart'; })
        .then(function () {
          btn.disabled = false;
          btn.textContent = original;
        });
    });
  }

  /* ------------------------------------------------------------------------
     Predictive search — falls back to a normal /search submit without JS
     ------------------------------------------------------------------------ */
  function initPredictiveSearch() {
    var forms = $$('[data-predictive-search]');
    if (!forms.length) return;

    forms.forEach(function (form) {
      var input = form.querySelector('input[type="search"]');
      var results = form.querySelector('[data-predictive-results]');
      if (!input || !results) return;

      var controller = null;
      var debounce = null;

      function clear() {
        results.innerHTML = '';
        results.hidden = true;
      }

      function run() {
        var q = input.value.trim();
        if (q.length < 2) { clear(); return; }
        if (controller) controller.abort();
        controller = new AbortController();

        var url = (Theme.routes.predictiveSearch || '/search/suggest') +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=product,page,article&resources[limit]=6' +
          '&section_id=predictive-search';

        fetch(url, { signal: controller.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var doc = new DOMParser().parseFromString(html, 'text/html');
            var payload = doc.querySelector('[data-predictive-payload]');
            results.innerHTML = payload ? payload.innerHTML : '';
            results.hidden = false;
          })
          .catch(function () { /* silent — the plain search form still works */ });
      }

      on(input, 'input', function () {
        window.clearTimeout(debounce);
        debounce = window.setTimeout(run, 220);
      });

      on(input, 'search', function () { if (!input.value) clear(); });
    });
  }

  /* ------------------------------------------------------------------------
     Collection toolbar — auto-submit sort and facet forms when JS is present
     ------------------------------------------------------------------------ */
  /* ------------------------------------------------------------------------
     Filter dropdowns — native <details>; JS only keeps one open at a time on
     desktop and closes them on an outside click or Escape.
     ------------------------------------------------------------------------ */
  function initFacets() {
    if (Theme.facetsBound) return;
    Theme.facetsBound = true;
    var desktop = window.matchMedia ? window.matchMedia('(min-width: 750px)') : { matches: true };

    document.addEventListener('toggle', function (e) {
      var d = e.target;
      if (!d.classList || !d.classList.contains('facet') || !d.open || !desktop.matches) return;
      $$('details.facet[open]').forEach(function (other) { if (other !== d) other.open = false; });
    }, true);

    on(document, 'click', function (e) {
      if (!desktop.matches || e.target.closest('details.facet')) return;
      $$('details.facet[open]').forEach(function (d) { d.open = false; });
    });

    on(document, 'keydown', function (e) {
      if (e.key !== 'Escape') return;
      $$('details.facet[open]').forEach(function (d) {
        d.open = false;
        var s = d.querySelector('summary');
        if (s) s.focus();
      });
    });
  }

  function initAutoSubmit() {
    on(document, 'change', function (e) {
      var el = e.target.closest ? e.target.closest('[data-auto-submit]') : null;
      if (!el) return;
      var form = el.closest('form');
      if (form) form.submit();
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */
  function init() {
    initOverlays();
    bindDisclosures();
    initDisclosures(document);
    initReveal();
    initStickyBuy();
    initMarquee();
    initEpisodes();
    initCartDrawer();
    initProductForms();
    initPredictiveSearch();
    initAutoSubmit();
    initFacets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run inside the theme editor when a section is re-rendered
  document.addEventListener('shopify:section:load', function (e) {
    initDisclosures(e.target || document);
    initReveal();
    initStickyBuy();
    initMarquee();
    initEpisodes();
    initPredictiveSearch();
  });
})();
